/**
 * Orders list table — rows, loading/empty states, pagination.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { TH_SX, TD_SX } from './orderManagementStyles';
import { StatusBadge, RemainingTimeCell } from './OrderManagementShared';
import { OrderDetailAccordion } from './OrderManagementDetailAccordion';

export function OrderManagementOrdersTable({
  loading,
  loadError,
  filteredOrders,
  displayedRows,
  expandedId,
  toggleRow,
  displayPage,
  setDisplayPage,
  rowsPerPage,
  setRowsPerPage,
  colors,
}) {
  return (
        <TableContainer sx={{ bgcolor: colors.white, overflowX: 'hidden' }}>
          <Table
            size='medium'
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              borderCollapse: 'collapse',
              borderSpacing: 0,
              '& .MuiTableCell-root': { boxSizing: 'border-box' },
            }}
          >
            <colgroup>
              <col style={{ width: '16%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '11%' }} />
            </colgroup>

            <TableHead>
              <TableRow>
                {['Order #', 'Customer', 'Kitchen', 'Type', 'Time Slot', 'Status', 'ETA', 'Remaining'].map(
                  (col, i) => (
                    <TableCell key={col} sx={{ ...TH_SX, px: i === 0 ? 3.5 : 2 }}>
                      {col}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {/* Loading */}
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 10, border: 'none' }}>
                    <CircularProgress size={26} sx={{ color: colors.ink }} />
                    <Typography
                      sx={{
                        mt: 1.5,
                        fontFamily: 'Inter Regular, sans-serif',
                        fontSize: 13,
                        color: colors.muted,
                      }}
                    >
                      Loading orders…
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {/* Empty state */}
              {!loading && !loadError && filteredOrders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 10, border: 'none' }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '16px',
                        bgcolor: colors.headerBg,
                        border: `1px solid ${colors.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <ReceiptLongOutlinedIcon sx={{ fontSize: 26, color: colors.muted }} />
                    </Box>
                    <Typography
                      sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 15, color: colors.ink, mb: 0.5 }}
                    >
                      No orders found
                    </Typography>
                    <Typography
                      sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: colors.muted }}
                    >
                      Try changing the date or filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!loading &&
                displayedRows.map((order) => {
                  const rowId = String(order._id || order.order_no || Math.random());
                  const isExpanded = expandedId === rowId;

                  const customerName =
                    `${order.cust_firstName ?? ''} ${order.cust_lastName ?? ''}`.trim() || '—';
                  const customerPhone = order.customer_phone_no ?? null;

                  // Display order number — show just the PAY suffix
                  const orderNoDisplay = order.order_no
                    ? (order.order_no.split('-').slice(1).join('-') || order.order_no)
                    : String(order._id || '—').slice(-8);

                  return (
                    <React.Fragment key={rowId}>
                      {/* ── Data row ── */}
                      <TableRow
                        hover
                        sx={{
                          transition: 'background-color 0.12s ease',
                          '&:hover': { bgcolor: '#F9FAFB !important' },
                          '&:last-child td': { borderBottom: 'none' },
                        }}
                      >
                        {/* Order # — click to expand */}
                        <TableCell
                          onClick={() => toggleRow(rowId)}
                          sx={{
                            ...TD_SX,
                            pl: 3.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            borderLeft: `3px solid ${isExpanded ? colors.ink : 'transparent'}`,
                            transition: 'border-left-color 0.12s ease',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: '10px',
                                bgcolor: isExpanded ? colors.ink : '#F3F4F6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'all 0.12s ease',
                              }}
                            >
                              <ReceiptLongOutlinedIcon
                                sx={{ fontSize: 15, color: isExpanded ? '#FFFFFF' : '#9CA3AF' }}
                              />
                            </Box>
                            <Box sx={{ overflow: 'hidden' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title={order.order_no ?? ''} placement='top' enterDelay={400} arrow>
                                  <Typography
                                    sx={{
                                      fontFamily: 'Inter Bold, sans-serif',
                                      fontWeight: 600,
                                      fontSize: 12.5,
                                      color: '#111827',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                      textOverflow: 'ellipsis',
                                      maxWidth: 110,
                                    }}
                                  >
                                    {orderNoDisplay}
                                  </Typography>
                                </Tooltip>
                                {isExpanded ? (
                                  <KeyboardArrowUpIcon sx={{ fontSize: 14, color: colors.ink, flexShrink: 0 }} />
                                ) : (
                                  <KeyboardArrowDownIcon sx={{ fontSize: 14, color: '#C4C9D4', flexShrink: 0 }} />
                                )}
                              </Box>
                              <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 10.5, color: '#9CA3AF' }}>
                                {order.delivery_date ?? '—'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Customer */}
                        <TableCell sx={TD_SX}>
                          <Typography
                            sx={{
                              fontFamily: 'Inter Bold, sans-serif',
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#111827',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              textTransform: 'capitalize',
                            }}
                          >
                            {customerName}
                          </Typography>
                          {customerPhone && (
                            <Typography
                              sx={{
                                fontFamily: 'Inter Regular, sans-serif',
                                fontSize: 11.5,
                                color: '#9CA3AF',
                                mt: 0.1,
                              }}
                            >
                              {customerPhone}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Kitchen */}
                        <TableCell sx={TD_SX}>
                          <Typography
                            sx={{
                              fontFamily: 'Inter Regular, sans-serif',
                              fontSize: 13,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {order.kitchen_name ?? order.kitchen_managingBranchName ?? '—'}
                          </Typography>
                        </TableCell>

                        {/* Delivery type */}
                        <TableCell sx={TD_SX}>
                          <Typography
                            sx={{
                              fontFamily: 'Inter Regular, sans-serif',
                              fontSize: 12,
                              color: '#374151',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {order.delivery_type
                              ? String(order.delivery_type)
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, (c) => c.toUpperCase())
                              : '—'}
                          </Typography>
                        </TableCell>

                        {/* Time slot */}
                        <TableCell sx={TD_SX}>
                          <Typography
                            sx={{
                              fontFamily: 'Inter Regular, sans-serif',
                              fontSize: 12,
                              color: '#374151',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {order.delivery_time_slot ?? '—'}
                          </Typography>
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={TD_SX}>
                          <StatusBadge status={order.internal_order_status} />
                        </TableCell>

                        {/* ETA */}
                        <TableCell sx={TD_SX}>
                          {order.ETA?.etaText ? (
                            <Tooltip
                              title={`${order.ETA.straightLineMiles ?? '—'} mi · ${order.ETA.etaMinutes ?? '—'} min drive`}
                              placement='top'
                              arrow
                            >
                              <Box
                                component='span'
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  bgcolor: '#F3F4F6',
                                  borderRadius: '20px',
                                  px: 1,
                                  py: 0.35,
                                  fontSize: 12,
                                  fontFamily: 'Inter Bold, sans-serif',
                                  fontWeight: 600,
                                  color: '#374151',
                                  cursor: 'default',
                                }}
                              >
                                <LocalShippingOutlinedIcon sx={{ fontSize: 12 }} />
                                {order.ETA.etaText}
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography
                              sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#D1D5DB' }}
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>

                        {/* Remaining time — live 1s countdown */}
                        <RemainingTimeCell order={order} />
                      </TableRow>

                      {/* ── Accordion detail ── */}
                      <TableRow sx={{ bgcolor: 'transparent' }}>
                        <TableCell
                          colSpan={8}
                          sx={{
                            p: 0,
                            width: '100%',
                            maxWidth: 0,
                            borderBottom: isExpanded ? `1px solid ${colors.border}` : 'none',
                            borderTop: 'none',
                            borderLeft: 'none',
                            borderRight: 'none',
                          }}
                        >
                          <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                            <OrderDetailAccordion order={order} colors={colors} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
            </TableBody>
          </Table>

          {/* Pagination */}
          <TablePagination
            component='div'
            count={filteredOrders.length}
            page={displayPage}
            onPageChange={(_, p) => setDisplayPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setDisplayPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage='Rows per page'
            sx={{
              borderTop: `1px solid ${colors.rowDivider}`,
              bgcolor: colors.headerBg,
              fontFamily: 'Inter Regular, sans-serif',
              color: colors.muted,
              '& .MuiTablePagination-toolbar': { minHeight: 50, px: 3 },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: 13,
                color: colors.muted,
                fontFamily: 'Inter Regular, sans-serif',
              },
              '& .MuiIconButton-root': {
                color: colors.muted,
                borderRadius: '8px',
                transition: 'all 0.15s ease',
              },
              '& .MuiIconButton-root:hover': { bgcolor: '#F3F4F6', color: colors.ink },
            }}
          />
        </TableContainer>
  );
}
